import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CandidateCommentProps } from './CandidateComment.types'
import styles from './CandidateComment.module.scss'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { useMutation, useQuery, useRequiredParams } from '@percent/partner-dashboard/common/hooks'
import { ButtonText, Flex, Loader, MessageInput } from '@percent/lemonade'
import { CommentBody } from './commentBody/CommentBody'

export function CandidateComment({ comment, isAdmin }: Readonly<CandidateCommentProps>) {
  // Using local state to avoid unnecessary API calls for reply count on the paginated query
  const [replyCount, setReplyCount] = useState<number>(comment.replyCount ?? 0)
  const [showReplies, setShowReplies] = useState(false)

  const { id: programId, candidateId } = useRequiredParams()
  const { t } = useTranslation()
  const { grantsService } = useServices()

  const { id } = comment

  const [{ data: replies, isLoading: areRepliesLoading }, { query }] = useQuery(
    grantsService.getCandidateCommentReplies,
    {
      programId,
      candidateId,
      commentId: id
    },
    false,
    false
  )

  const refreshReplies = useCallback(() => {
    query({ programId, candidateId, commentId: id })
  }, [query, programId, candidateId, id])

  const [{ isLoading: isReplyLoading }, { apiFunc: replyFunc }] = useMutation(
    grantsService.createCandidateComment,
    () => {
      setReplyCount(prev => prev + 1)
      refreshReplies()
    }
  )

  const onSendReply = (content: string) => {
    replyFunc({ programId, candidateId, content, parentId: id, visibility: 'public' })
  }

  const toggleReplies = () => {
    setShowReplies(prev => !prev)
    refreshReplies()
  }

  const getButtonText = () => {
    if (showReplies) {
      return t('grants.candidateNotes.hideReplies')
    }

    if (replyCount > 0) {
      return t('grants.candidateNotes.viewReplies', { count: replyCount })
    }

    return t('grants.candidateNotes.reply')
  }

  return (
    <Flex direction="column" gap={8} py={16} className={styles.comment}>
      <CommentBody comment={comment} isAdmin={isAdmin} />
      <Flex direction="column" pl={32} mt={4}>
        <ButtonText variant="default" size="small" onClick={toggleReplies} data-testid="comment-reply-button">
          {getButtonText()}
        </ButtonText>

        {showReplies && (
          <Flex direction="column" gap={12} pl={0}>
            {areRepliesLoading && (
              <Flex className={styles.replyLoaderWrapper}>
                <Loader />
              </Flex>
            )}

            <Flex className={styles.repliesWrapper}>
              {replies?.map(reply => (
                <CommentBody key={reply.id} comment={reply} isAdmin={false} />
              ))}
            </Flex>
            <MessageInput onSend={onSendReply} isLoading={isReplyLoading} />
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}
